import { Alert, Form, Input, Modal, Space } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface ReasonModalProps {
  open: boolean
  title: string
  warning: string
  okText: string
  onSubmit: (reason: string) => Promise<void>
  onClose: () => void
}

interface FormValues {
  reason: string
}

/** Bekor qilish va storno — sabab majburiy, natija (xato ham) oynaning ichida. */
export function ReasonModal({ open, title, warning, okText, onSubmit, onClose }: ReasonModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const submit = useMutation({
    mutationFn: (reason: string) => onSubmit(reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    form.resetFields()
    submit.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true }}
      confirmLoading={submit.isPending}
      onCancel={close}
      destroyOnHidden
      onOk={() => submitOnValid(form, (values) => submit.mutate(values.reason.trim()))}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="warning" showIcon message={warning} />
        {submit.isError ? <Alert type="error" showIcon message={translateError(submit.error, t)} /> : null}
        <Form form={form} layout="vertical" requiredMark preserve={false}>
          <Form.Item
            name="reason"
            label={t('actions.reason')}
            rules={[{ required: true, whitespace: true, message: t('stakeholder.validation.reason') }]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount autoFocus />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
